import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApi } from '../state/ApiContext';
import {
  PermissionLevel,
  PermissionResource,
  levelAtLeast,
} from './permission-catalog';
import type { EffectivePermissions } from '../client';

export interface PermissionsState {
  /** Raw payload from `GET /api/teams/:teamId/permissions/me`, once loaded.
   *  `null` while loading or after a failed fetch — never a stand-in for
   *  "grants everything". */
  effective: EffectivePermissions | null;
  /** True until the first fetch for the current team settles (success or
   *  failure). Callers that gate UI on a permission should hold their
   *  render (e.g. show nothing/a spinner) while this is true rather than
   *  rendering the denied state, which would flash "you can't do this"
   *  for a fraction of a second on every page load. */
  isLoading: boolean;
  /** Set when the fetch itself failed (network error, 5xx, etc). Access is
   *  never granted on error — this is surfaced so a caller can tell "we
   *  don't know" apart from "we asked and the answer was no". */
  error: string | null;
}

const PermissionsContext = createContext<PermissionsState | undefined>(
  undefined,
);

interface PermissionsProviderProps {
  /** The team whose effective permissions this subtree needs. Accepts the
   *  string form `useParams()` returns as well as a parsed number. */
  teamId: string | number | undefined;
  children: React.ReactNode;
}

/**
 * Fetches the caller's effective permissions for one team exactly once (and
 * again if `teamId` changes), and holds them for every `usePermission` /
 * `useAnyProjectPermission` call in the subtree.
 *
 * A team member with zero role assignments is a valid, reachable state (see
 * the design spec) — they get back `{ team: {}, projects: {} }`, not an
 * error, and every permission check below correctly resolves to `false`.
 */
export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  teamId,
  children,
}) => {
  const { rolesApi } = useApi();
  const [state, setState] = useState<PermissionsState>({
    effective: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const numericTeamId =
      typeof teamId === 'number' ? teamId : parseInt(teamId ?? '', 10);
    if (!Number.isFinite(numericTeamId) || numericTeamId <= 0) {
      setState({ effective: null, isLoading: false, error: 'Invalid team id' });
      return;
    }

    let cancelled = false;
    setState({ effective: null, isLoading: true, error: null });

    if (!rolesApi) {
      // Defensive: a caller with no `rolesApi` (e.g. a test double that
      // doesn't set one up) fails closed exactly like a network error
      // rather than throwing out of an effect and tearing down the tree.
      setState({
        effective: null,
        isLoading: false,
        error: 'Permissions API client unavailable.',
      });
      return;
    }

    rolesApi
      .apiTeamsTeamIdPermissionsMeGet(numericTeamId)
      .then((response) => {
        if (cancelled) return;
        setState({ effective: response.data, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching effective permissions:', err);
        // Fail CLOSED: never fall back to a permissive default. `effective`
        // stays null, so every usePermission() call below returns false.
        setState({
          effective: null,
          isLoading: false,
          error: 'Failed to load your permissions for this team.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [rolesApi, teamId]);

  return (
    <PermissionsStateProvider value={state}>
      {children}
    </PermissionsStateProvider>
  );
};

/**
 * Supplies a permissions state directly, with no fetch.
 *
 * `PermissionsProvider` needs `useApi().rolesApi`, which makes rendering a
 * guarded subtree in isolation (one board cell, one card modal) mean stubbing
 * an API client that has nothing to do with the thing under test. This takes
 * the exact same `PermissionsState` the fetch produces, so every
 * `usePermission` call below — team map, per-project map, ladder, the lot —
 * runs unchanged. Used by the tests, and by `PermissionsProvider` itself so
 * there is only one Provider element in the codebase.
 */
export const PermissionsStateProvider: React.FC<{
  value: PermissionsState;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <PermissionsContext.Provider value={value}>
    {children}
  </PermissionsContext.Provider>
);

/** Raw access to the fetch state, for callers that need `isLoading`/`error`
 *  directly (e.g. to hold a render, or to surface a banner) rather than a
 *  single collapsed boolean. */
export function useEffectivePermissions(): PermissionsState {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error(
      'useEffectivePermissions must be used within a PermissionsProvider',
    );
  }
  return context;
}

/**
 * True when the caller holds at least `level` on `resource`, using ladder
 * semantics (a held `manage` satisfies a required `edit` or `view`).
 *
 * Consults the team-scoped map first; when `projectId` is passed, also
 * consults that project's entry in the per-project map and returns true if
 * either satisfies the requirement (most-permissive-wins, same as the
 * resolver). Returns `false` while loading and on fetch failure — this
 * hook never distinguishes "not yet known" from "denied" on its own; use
 * `useEffectivePermissions().isLoading` when that distinction matters.
 */
export function usePermission(
  resource: PermissionResource,
  level: PermissionLevel,
  projectId?: number | string | null,
): boolean {
  const { effective } = useEffectivePermissions();
  return useMemo(() => {
    if (!effective) return false;
    if (levelAtLeast(effective.team[resource], level)) return true;
    if (projectId === undefined || projectId === null) return false;
    const projectMap = effective.projects[String(projectId)];
    if (!projectMap) return false;
    return levelAtLeast(projectMap[resource], level);
  }, [effective, resource, level, projectId]);
}

/**
 * The "any project" rule: true when the caller holds `level` on `resource`
 * at team scope, OR on at least one project in the per-project map. This is
 * the client-side mirror of the backend's `requireAnyProjectPermission` —
 * it is what lets a contractor whose only grant is scoped to one board still
 * see the Projects/Files tabs, filtered to that board, rather than losing
 * the tab entirely because they hold nothing at team scope.
 */
export function useAnyProjectPermission(
  resource: PermissionResource,
  level: PermissionLevel,
): boolean {
  const { effective } = useEffectivePermissions();
  return useMemo(() => {
    if (!effective) return false;
    if (levelAtLeast(effective.team[resource], level)) return true;
    return Object.values(effective.projects).some((projectMap) =>
      levelAtLeast(projectMap[resource], level),
    );
  }, [effective, resource, level]);
}
